/**
 * Política CENTRAL de upload (segurança) — fonte única de verdade dos tipos e
 * tamanhos aceitos no cliente.
 *
 * IMPORTANTE: validação no cliente é UX/conveniência e é BURLÁVEL (curl/Burp
 * direto no endpoint). A validação AUTORITATIVA é server-side (API .NET +
 * API Gateway/Lambdas). Aqui alinhamos o front à política acordada e removemos
 * vetores perigosos (SVG/HTML) do allowlist para não induzir o usuário a algo
 * que o servidor vai (deve) rejeitar.
 */
const MB = 1024 * 1024;
const GB = 1024 * MB;

export type UploadCategory = 'avatar' | 'postImage' | 'postVideo' | 'projectPhoto' | 'datalog';

export interface UploadRule {
  /** Extensões permitidas (minúsculas, sem ponto). */
  exts: string[];
  /** Tamanho máximo em bytes. */
  maxBytes: number;
  /** Valor pronto para o atributo accept="" (extensões + MIMEs). */
  accept: string;
}

// SVG e HTML ficam DE FORA de propósito (SVG = vetor de Stored XSS). tif/tiff também
// não entram (fora da política e mal suportados em <img>).
const AVATAR_EXTS = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'avif'];
const PUBLIC_IMG_EXTS = ['jpg', 'jpeg', 'png', 'webp'];
const DATALOG_EXTS = ['ftlog', 'ftml', 'ftmap', 'ftm'];

function accept(exts: string[], mimes: string[]): string {
  return [...exts.map((e) => '.' + e), ...mimes].join(',');
}

const IMG_MIMES_AVATAR = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/avif'];
const IMG_MIMES_PUBLIC = ['image/jpeg', 'image/png', 'image/webp'];

export const UPLOAD_RULES: Record<UploadCategory, UploadRule> = {
  avatar: { exts: AVATAR_EXTS, maxBytes: 10 * MB, accept: accept(AVATAR_EXTS, IMG_MIMES_AVATAR) },
  postImage: { exts: PUBLIC_IMG_EXTS, maxBytes: 10 * MB, accept: accept(PUBLIC_IMG_EXTS, IMG_MIMES_PUBLIC) },
  projectPhoto: { exts: PUBLIC_IMG_EXTS, maxBytes: 15 * MB, accept: accept(PUBLIC_IMG_EXTS, IMG_MIMES_PUBLIC) },
  postVideo: { exts: ['mp4'], maxBytes: 200 * MB, accept: '.mp4,video/mp4' },
  datalog: { exts: DATALOG_EXTS, maxBytes: 5 * GB, accept: accept(DATALOG_EXTS, []) },
};

/** accept="" combinado de imagem + vídeo para o compositor de post. */
export const ACCEPT_POST_MEDIA = [UPLOAD_RULES.postImage.accept, UPLOAD_RULES.postVideo.accept].join(',');

export function fileExt(name: string): string {
  return name.split('.').pop()?.toLowerCase() ?? '';
}

export type UploadCheckReason = 'type' | 'size';
export interface UploadCheck {
  ok: boolean;
  reason?: UploadCheckReason;
  /** Lista de extensões permitidas (para a mensagem de erro). */
  allowed?: string;
  /** Tamanho máximo em MB (para a mensagem de erro). */
  maxMb?: number;
}

/** Valida extensão + tamanho de um arquivo contra a categoria. */
export function checkUpload(file: File, category: UploadCategory): UploadCheck {
  const rule = UPLOAD_RULES[category];
  const allowed = rule.exts.join(', ');
  const maxMb = Math.round(rule.maxBytes / MB);
  if (!rule.exts.includes(fileExt(file.name))) return { ok: false, reason: 'type', allowed, maxMb };
  if (file.size > rule.maxBytes) return { ok: false, reason: 'size', allowed, maxMb };
  return { ok: true, allowed, maxMb };
}

/**
 * Traduz uma rejeição em chaves i18n + parâmetro, para o componente montar o toast
 * (desacoplado do transloco/PrimeNG). Uso:
 *   const m = uploadRejectI18n(chk);
 *   messageService.add({ severity:'error', summary: t(m.summaryKey), detail: t(m.detailKey, {'0': m.param}) });
 */
export function uploadRejectI18n(chk: UploadCheck): { summaryKey: string; detailKey: string; param: string | number } {
  return chk.reason === 'size'
    ? { summaryKey: 'socialFeed.fileTooLarge', detailKey: 'socialFeed.fileTooLargeDetail', param: chk.maxMb ?? 0 }
    : { summaryKey: 'socialFeed.invalidImageType', detailKey: 'socialFeed.invalidImageTypeDetail', param: chk.allowed ?? '' };
}
